import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import RegistryDrawer from "@/components/RegistryDrawer";
import { recordRecent } from "@/lib/recents";
import { logBecoTelemetry } from "@/lib/coachVoice";
import ProcessLog from "@/components/ProcessLog";
import PromptCoach from "@/components/PromptCoach";
import UploadZone from "@/components/UploadZone";
import RegionPlan, { ObligationsDiff } from "@/components/RegionPlan";
import ComplianceScope from "@/components/ComplianceScope";
import ComplianceRecommendation from "@/components/ComplianceRecommendation";
import ProjectJourney from "@/components/ProjectJourney";
import { Checkpoints, PublishGate } from "@/components/Checkpoints";
import CoachLibrary from "@/components/CoachLibrary";
import MindsetTab from "@/components/MindsetTab";
import HostingPanel from "@/components/HostingPanel";
import BuilderTab from "@/components/BuilderTab";
import ManageTab from "@/components/ManageTab";
import CodeReviewTab from "@/components/CodeReviewTab";
import { ReferralCard } from "@/components/ReferralCard";
import CreditsDialog from "@/components/CreditsDialog";
import GenerateTab from "@/components/GenerateTab";
import MobileTargets from "@/components/MobileTargets";
import CompareMarkets from "@/components/CompareMarkets";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Gauge, ListChecks, Hammer, Clock, SlidersHorizontal, Sparkles, Smartphone, ScrollText, Server, Code2, Rocket, BookOpen, Github, PanelLeft, PanelRight, Bot, X } from "lucide-react";
import { Glossary, InfoTip, LastVerified } from "@/components/kit";

const ICON = 14; // Patch 68 — every toolbar / feature icon matches the preview-box icon size.

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid} style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        {Icon && <Icon size={18} color="var(--accent)" />}
        <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

const TABS = [["build", "Build", Hammer], ["manage", "Manage", SlidersHorizontal], ["generate", "Generate", Sparkles], ["mobile", "Mobile", Smartphone], ["coach", "Coach library", BookOpen], ["telemetry", "Telemetry", Gauge], ["engine", "Engine log", ScrollText], ["hosting", "Hosting & data", Server], ["code", "Code review", Code2], ["ship", "Checkpoints & Publish", Rocket]];
const FEATURE_TITLES = {
  coach: "BeCo Coach",
  perimeter: "Regulatory perimeter gate",
  crx: "Compliance Risk Index",
  quote: "Fixed-price quote & sandbox build",
  premortem: "Compliance pre-mortem",
  details: "Compliance & project details",
};

export default function Project() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [tab, setTab] = useState("build");
  const [featureModal, setFeatureModal] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [desc, setDesc] = useState("");
  const [drawer, setDrawer] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [assessment, setAssessment] = useState(null);
  const [quote, setQuote] = useState(null);
  const [build, setBuild] = useState(null);
  const [premortem, setPremortem] = useState(null);
  const [crx, setCrx] = useState(null);
  const [diff, setDiff] = useState(null);
  const [badges, setBadges] = useState([]);
  const [descHist, setDescHist] = useState({ past: [], future: [] });
  const [perimIdle, setPerimIdle] = useState(false);

  // #36 undo/redo on the spec text
  const setDescTracked = (next) => {
    setDescHist(h => ({ past: [...h.past, desc].slice(-50), future: [] }));
    setDesc(prev => (typeof next === "function" ? next(prev) : next));
  };
  const undo = () => setDescHist(h => {
    if (!h.past.length) return h;
    const prev = h.past[h.past.length - 1];
    setDesc(cur => { setDescHist(hh => ({ past: hh.past.slice(0, -1), future: [cur, ...hh.future] })); return prev; });
    return h;
  });
  const redo = () => setDescHist(h => {
    if (!h.future.length) return h;
    const nxt = h.future[0];
    setDesc(cur => { setDescHist(hh => ({ past: [...hh.past, cur], future: hh.future.slice(1) })); return nxt; });
    return h;
  });

  const loadProject = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}`); setProject(data); setDesc(data.description || "");
      recordRecent({ type: "screen", id: `project-${id}`, label: data.name, to: `/workspace/project/${id}` });
      api.get(`/orgs/${data.org_id}/projects`).then(r => setSiblings(r.data || [])).catch(() => {}); }
    catch (e) { toast.error(errMsg(e)); }
  }, [id]);

  useEffect(() => {
    loadProject();
    api.get("/prefs").then(r => setPrefs(r.data)).catch(() => {});
    api.get("/perimeter/questions").then(r => setQuestions(r.data)).catch(() => {});
    api.get(`/projects/${id}/pre-mortem`).then(r => setPremortem(r.data)).catch(() => {});
    api.get(`/projects/${id}/crx`).then(r => setCrx(r.data)).catch(() => {});
    api.get(`/coach/projects/${id}/concept-badges`, { params: { prompt: "" } }).then(r => setBadges(r.data.badges)).catch(() => {});
  }, [id, loadProject]);

  useEffect(() => {
    if (desc) api.get(`/coach/projects/${id}/concept-badges`, { params: { prompt: desc } }).then(r => setBadges(r.data.badges)).catch(() => {});
  }, [desc, id]);

  // SW10 — if idle on the Perimeter Gate, surface the Copilot affordance (static, never a popup).
  useEffect(() => {
    if (assessment) { setPerimIdle(false); return; }
    setPerimIdle(false);
    const t = setTimeout(() => setPerimIdle(true), 45000);
    return () => clearTimeout(t);
  }, [answers, assessment]);

  const runPerimeter = async () => {
    try {
      const { data } = await api.post("/perimeter/assess", { project_id: id, answers });
      setAssessment(data); loadProject();
      api.get(`/projects/${id}/crx`).then(r => setCrx(r.data));
      logBecoTelemetry({ project_id: id, kind: "perimeter_assessed", scope_boundary_set: true,
        after_setback: data.outcome === "red" || data.outcome === "amber" });
      toast.success(`Perimeter: ${data.outcome.toUpperCase()}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const acknowledgeRed = async () => {
    try { await api.post(`/perimeter/${assessment.id}/acknowledge`); toast.success("Red items acknowledged"); loadProject(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const getQuote = async () => {
    try { const { data } = await api.post("/quotes", { project_id: id, complexity: 5, feature_count: 3 }); setQuote(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const confirmQuote = async () => {
    try { await api.post(`/quotes/${quote.id}/confirm`); setQuote({ ...quote, status: "confirmed" }); toast.success("Quote confirmed"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runBuild = async () => {
    try { const { data } = await api.post("/builds/sandbox", { project_id: id, quote_id: quote.id }); setBuild(data);
      toast.success("Sandbox build finished"); loadProject(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!project) return <Shell active="workspace"><p className="k-muted">Loading…</p></Shell>;
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const outcome = assessment?.outcome || project.perimeter_outcome;
  const outColor = { green: "k-tag-green", amber: "k-tag-warn", red: "k-tag-red" };
  const riskCard = (r, i) => (
    <div key={i} className="k-control" style={{ marginBottom: 8 }}>
      <div className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>{r.reason}</div>
      <div className="k-muted" style={{ fontSize: 11 }}>Cites {r.rule_id} · {r.reviewer}</div>
      <div style={{ marginTop: 4 }}><LastVerified date={r.last_reviewed} prefix="Rule reviewed" testid={`premortem-verified-${i}`} /></div>
    </div>
  );

  // The six builder features, previously large panels below the boxes, now open from the prompt-box icon strip.
  const renderFeature = (key) => {
    if (key === "coach") return (
      <div data-testid="coach-voice-block" style={{ borderLeft: "3px solid var(--accent-secondary, #a78bfa)", background: "var(--canvas)", borderRadius: 8, padding: "10px 12px" }}>
        <div className="k-heading" style={{ fontSize: 12, fontWeight: 700, color: "var(--accent-secondary, #a78bfa)", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--accent-secondary, #a78bfa)", display: "inline-block" }} />
          BeCo Coach · noticing your working pattern
        </div>
        <PromptCoach prompt={desc} projectId={id} onAppend={(s) => setDescTracked(desc + s)} onSpecConfirmed={(s) => setDescTracked(s)} />
      </div>
    );
    if (key === "perimeter") return (
      <Section title={<Glossary term="Perimeter Gate">Regulatory perimeter gate</Glossary>} icon={ShieldAlert} testid="perimeter-section">
        <details data-testid="perimeter-questions">
          <summary className="k-link" data-testid="perimeter-questions-toggle" style={{ cursor: "pointer", fontSize: 13, marginBottom: 8 }}>
            Answer the perimeter questions ({questions.length})
          </summary>
          <div style={{ marginTop: 10 }}>
            {questions.map(q => (
              <label key={q.id} className="k-muted" style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, marginBottom: 8 }}>
                <input type="checkbox" data-testid={`perimeter-q-${q.id}`} checked={!!answers[q.id]}
                  onChange={e => setAnswers({ ...answers, [q.id]: e.target.checked })} />
                <span>{q.q} <span className={`k-tag-${q.risk === "red" ? "red" : q.risk === "amber" ? "warn" : "info"}`}>[{q.risk}]</span>
                  {q.why && <InfoTip label="Why we ask this" text={q.why} testid={`perimeter-why-${q.id}`} />}
                </span>
              </label>
            ))}
          </div>
        </details>
        <button className="k-btn k-btn-primary" data-testid="run-perimeter" style={{ marginTop: 8 }} onClick={runPerimeter}>Assess</button>
        {perimIdle && !assessment && !outcome && (
          <p data-testid="perimeter-copilot-hint" className="k-muted" style={{ fontSize: 12, marginTop: 10 }}>
            Not sure how to answer? <button className="k-link" data-testid="perimeter-ask-copilot"
              onClick={() => window.dispatchEvent(new Event("summon-copilot"))}
              style={{ background: "none", border: "none", padding: 0, font: "inherit", cursor: "pointer" }}>Ask the Kadana Copilot</button> — it can explain any question.
          </p>
        )}
        {(assessment || outcome) && (
          <div className="k-control" style={{ marginTop: 14 }} data-testid="perimeter-result">
            <div className={outColor[outcome]} style={{ fontWeight: 700 }}>Outcome: {outcome?.toUpperCase()}</div>
            {assessment?.red?.length > 0 && <div className="k-tag-red" style={{ fontSize: 13, marginTop: 6 }}>Red: {assessment.red.join("; ")}</div>}
            {assessment?.amber?.length > 0 && <div className="k-tag-warn" style={{ fontSize: 13, marginTop: 6 }}>Amber: {assessment.amber.join("; ")}</div>}
            <p className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>{assessment?.note}</p>
            {assessment?.outcome === "red" && !assessment?.acknowledged_red &&
              <button className="k-btn k-btn-danger" data-testid="ack-red" onClick={acknowledgeRed}>Acknowledge red (confirm authorisation)</button>}
          </div>
        )}
      </Section>
    );
    if (key === "crx") return (
      <Section title={<Glossary term="CRX">Compliance Risk Index</Glossary>} icon={Gauge} testid="crx-section">
        {crx && crx.evaluated === false ? (
          <div data-testid="crx-not-evaluated">
            <span className="k-badge k-badge-sandbox" style={{ fontSize: 14 }}>{crx.badge}</span>
            <p className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>{crx.note}</p>
          </div>
        ) : crx && crx.crx != null ? (<>
          <div className="k-heading" style={{ fontSize: 36, fontWeight: 700 }}>{crx.crx}</div>
          <p className="k-muted" style={{ fontSize: 12 }}>{crx.note}</p>
        </>) : <p className="k-muted">—</p>}
      </Section>
    );
    if (key === "quote") return (
      <Section title="Fixed-price quote & sandbox build" icon={Hammer} testid="build-section">
        {!quote && <button className="k-btn k-btn-primary" data-testid="get-quote" onClick={getQuote}>Get fixed quote</button>}
        {quote && (
          <div className="k-control" data-testid="quote-card">
            <div className="k-heading" style={{ fontSize: 24, fontWeight: 700 }}>{Number(quote.quote_credits).toLocaleString("en-GB")} credits</div>
            {quote.estimated_build_time && (
              <p className="k-muted" data-testid="quote-build-time" style={{ fontSize: 13, margin: "4px 0", display: "flex", alignItems: "center", gap: 6 }}>
                <Clock size={13} /> Estimated build time: {quote.estimated_build_time.label}
              </p>
            )}
            <p className="k-muted" style={{ fontSize: 13, margin: "4px 0" }}>Estimated charge for this build, agreed before we start.</p>
            {diff && <p className="k-muted" style={{ fontSize: 12 }}>Obligations diff is indicative, not legal advice — review before confirming.</p>}
            <span className="k-badge k-badge-sandbox">SANDBOX</span>
            <div style={{ marginTop: 8 }}>
              <a className="k-link" data-testid="view-billing-detail" href="/billing" style={{ fontSize: 12 }}>View billing detail →</a>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              {quote.status !== "confirmed" && <button className="k-btn k-btn-primary" data-testid="confirm-quote" onClick={confirmQuote}>Confirm quote</button>}
              {quote.status === "confirmed" && !build && <button className="k-btn k-btn-primary" data-testid="run-build" onClick={runBuild}>Run sandbox build</button>}
            </div>
          </div>
        )}
        {build && (
          <div className="k-control" data-testid="build-result" style={{ marginTop: 12 }}>
            <div className="k-heading" style={{ fontWeight: 600 }}>Build {build.status} <span className="k-badge k-badge-sandbox">SANDBOX</span></div>
            <p className="k-muted" style={{ fontSize: 13 }}>{build.message}</p>
            {build.build_id && <p className="k-muted" data-testid="build-estimate-note" style={{ fontSize: 12, marginTop: 10 }}>Costs are metered against your estimate; Kadana does not offer discretionary refunds.</p>}
          </div>
        )}
      </Section>
    );
    if (key === "premortem") return (
      <>
        {badges.length > 0 && (
          <div data-testid="concept-badges" style={{ marginBottom: 16 }}>
            <div className="k-heading" style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Concept badges ({badges.length})</div>
            {badges.map(b => (
              <div key={b.id} className="k-control" data-testid={`concept-badge-${b.id}`} style={{ marginBottom: 8 }}>
                <div className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>{b.label} <span className="k-badge">{b.metric}</span></div>
                <div className="k-muted" style={{ fontSize: 13 }}>{b.plain}</div>
                <div className="k-muted" style={{ fontSize: 11, marginTop: 4 }}>Why am I seeing this? {b.why} · <span className="k-link">Turn off ({b.turn_off_feature_id})</span></div>
              </div>
            ))}
          </div>
        )}
        <Section title="Compliance pre-mortem" icon={ListChecks} testid="premortem-section">
          {(premortem?.top_risks || []).slice(0, 2).map((r, i) => riskCard(r, i))}
          {(premortem?.top_risks?.length || 0) > 2 && (
            <details data-testid="premortem-more">
              <summary className="k-link" data-testid="premortem-more-toggle" style={{ cursor: "pointer", fontSize: 12, marginBottom: 8 }}>
                Show all {premortem.top_risks.length} risks
              </summary>
              <div style={{ marginTop: 8 }}>{premortem.top_risks.slice(2).map((r, i) => riskCard(r, i + 2))}</div>
            </details>
          )}
          <p className="k-muted" style={{ fontSize: 11 }}>{premortem?.note}</p>
        </Section>
      </>
    );
    if (key === "details") return (
      <div data-testid="details-compliance-group">
        <ComplianceScope projectId={id} onChange={d => { setDiff(d.obligations_diff); loadProject(); api.get(`/projects/${id}/crx`).then(r => setCrx(r.data)); api.get(`/projects/${id}/pre-mortem`).then(r => setPremortem(r.data)); }} />
        <RegionPlan projectId={id} project={project} onDiff={d => { setDiff(d); api.get(`/projects/${id}/crx`).then(r => setCrx(r.data)); }} />
        <ObligationsDiff diff={diff} />
        <ComplianceRecommendation projectId={id} />
        <ProjectJourney projectId={id} />
        <CompareMarkets />
        <UploadZone projectId={id} />
      </div>
    );
    return null;
  };

  const tabBtn = (k, label, Icon) => (
    <button key={k} data-testid={`tab-${k}`} title={label} aria-label={label}
      className={`k-btn ${tab === k ? "k-btn-primary" : "k-btn-ghost"}`}
      style={{ padding: "6px 8px", minHeight: 0 }} onClick={() => { setTab(k); setFeatureModal(null); }}>
      <Icon size={ICON} />
    </button>
  );

  // Desktop top toolbar — the view tabs (icon-only) plus the build box controls, on the logo/search level.
  const viewToolbar = (
    <>
      <div style={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
        {TABS.map(([k, l, Icon]) => tabBtn(k, l, Icon))}
      </div>
      {tab === "build" && (
        <div style={{ display: "flex", gap: 3, alignItems: "center", marginLeft: 6, borderLeft: "1px solid var(--border)", paddingLeft: 6 }}>
          <button className="k-btn k-btn-ghost" data-testid="tb-github" title="Save to GitHub" aria-label="Save to GitHub" style={{ padding: "6px 8px", minHeight: 0 }} onClick={() => window.dispatchEvent(new Event("kadana-save-github"))}><Github size={ICON} /></button>
          <button className="k-btn k-btn-ghost" data-testid="tb-max-left" title="Maximise prompt column" aria-label="Maximise prompt column" style={{ padding: "6px 8px", minHeight: 0 }} onClick={() => window.dispatchEvent(new Event("kadana-toggle-left"))}><PanelLeft size={ICON} /></button>
          <button className="k-btn k-btn-ghost" data-testid="tb-max-right" title="Maximise preview column" aria-label="Maximise preview column" style={{ padding: "6px 8px", minHeight: 0 }} onClick={() => window.dispatchEvent(new Event("kadana-toggle-right"))}><PanelRight size={ICON} /></button>
          <button className="k-btn k-btn-ghost" data-testid="tb-copilot" title="Socratic AI Co-Pilot" aria-label="Summon Socratic AI Co-Pilot" style={{ padding: "6px 8px", minHeight: 0 }} onClick={() => window.dispatchEvent(new Event("summon-copilot"))}><Bot size={ICON} /></button>
        </div>
      )}
    </>
  );

  return (
    <Shell active="workspace" toolbar={!isMobile ? viewToolbar : null} bare={tab === "build" && !isMobile}>
      {isMobile && (
        <div data-testid="project-tabs" style={{ display: "flex", gap: 6, marginBottom: 12, overflowX: "auto", paddingBottom: 6 }}>
          {TABS.map(([k, l, Icon]) => (
            <button key={k} data-testid={`tab-${k}`} className={`k-badge ${tab === k ? "k-badge-accent" : ""}`}
              style={{ cursor: "pointer", padding: "8px 12px", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}
              onClick={() => setTab(k)}><Icon size={13} />{l}</button>
          ))}
        </div>
      )}

      {tab === "build" && (
        <BuilderTab projectId={id} project={project} desc={desc} onDesc={setDescTracked} prefs={prefs} onReload={loadProject}
          onFeature={setFeatureModal} onUndo={undo} onRedo={redo} canUndo={descHist.past.length > 0} canRedo={descHist.future.length > 0}
          onSearch={() => setDrawer(true)} />
      )}

      {tab !== "build" && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h1 className="k-heading" style={{ fontSize: 22, margin: 0 }}>{project.name}</h1>
          <span className="k-badge">{project.status}</span>
        </div>
      )}

      {tab === "manage" && <ManageTab projectId={id} />}
      {tab === "generate" && <GenerateTab projectId={id} />}
      {tab === "mobile" && <MobileTargets projectId={id} />}
      {tab === "coach" && <CoachLibrary projectId={id} />}
      {tab === "telemetry" && <MindsetTab projectId={id} />}
      {tab === "engine" && <ProcessLog projectId={id} mode={prefs?.explanation_mode} />}
      {tab === "hosting" && <HostingPanel projectId={id} />}
      {tab === "code" && <CodeReviewTab projectId={id} />}
      {tab === "ship" && (<><Checkpoints projectId={id} /><PublishGate projectId={id} onPublished={loadProject} />
        <div style={{ marginTop: 16 }}>
          <ReferralCard context="deploy" appName={project?.name || ""} appDescription={project?.description || ""}
            onUpgrade={() => nav("/billing")} />
        </div></>)}

      {featureModal && (
        <div data-testid="feature-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 120, display: "flex", overflowY: "auto" }} onClick={() => setFeatureModal(null)}>
          <div className="k-container k-fadein" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 680, width: "calc(100% - 32px)", margin: "40px auto", maxHeight: "85vh", overflowY: "auto", padding: 24, alignSelf: "flex-start" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 className="k-heading" data-testid="feature-modal-title" style={{ fontSize: 18, margin: 0 }}>{FEATURE_TITLES[featureModal]}</h3>
              <button className="k-btn k-btn-ghost" data-testid="feature-modal-close" aria-label="Close" style={{ padding: "4px 8px", minHeight: 0 }} onClick={() => setFeatureModal(null)}><X size={16} /></button>
            </div>
            {renderFeature(featureModal)}
          </div>
        </div>
      )}

      <CreditsDialog open={creditsOpen} onClose={() => setCreditsOpen(false)} />

      <RegistryDrawer open={drawer} onClose={() => setDrawer(false)} projectId={id}
        onAdded={d => { setDiff(d.obligations_diff); setDrawer(false); toast.success("Added to project"); }} />
    </Shell>
  );
}
